// @flow

// Extending ObservationBlob type
//
// Destructuring type annotations isn't supported in babel-eslint
// due to lack of support in babylon@6.x.x (https://github.com/babel/babel-eslint/issues/443)
// We ignore the type def, and use flow comment type def syntax
// we also instruct prettier to ignore this line since it strips flow comments type, shit
import { OrderedSet as Set, Map } from 'immutable';

/* eslint-disable */
// prettier-ignore
type ObservationRecordShape = {
  /* :: ...ObservationBlob, */
  /* :: ...ObservationTypeBlob, */
  selected: boolean,
};
/* eslint-enable */

export type ObservationTypeCode = string;
export type ObservationData = {
  comments: ?string,
};

type Comparable = string | { observation_type_code: string };

export default class Observation {
  +name: string;
  +observation_id: ObservationTypeCode = '';
  +observation_type_code: string = '';
  +catalog_id: Id = -1;
  +client_id: Id = -1;
  +ugc_id: UgcId = -1;
  +user_data: {
    user_id: number | string,
    name: string,
    email: string,
  } = {
    user_id: -1,
    name: '',
    email: '',
  };
  +observation_data: ObservationData = {
    comments: '',
  };
  +created_date: string = '';
  +updated_date: string = '';
  +selected: boolean = false;
  +custom_observation: boolean = false;

  constructor(data: ObservationRecordShape) {
    const _ = (this: any);
    _.name = data.name;
    _.ugc_id = data.ugc_id || -1;
    _.observation_type_code = data.observation_type_code;
    _.observation_id = data.observation_id;
    _.catalog_id = data.catalog_id;
    _.client_id = data.client_id;
    _.user_data = data.user_data;
    _.observation_data = data.observation_data;
    _.created_date = data.created_date;
    _.updated_date = data.updated_date;
    _.selected = data.selected;
    _.custom_observation = data.custom_observation || false;
  }

  equals(other: Observation) {
    return (
      this.name === other.name &&
      this.ugc_id === other.ugc_id &&
      this.observation_type_code === other.observation_type_code &&
      this.selected === other.selected &&
      this.client_id === other.client_id &&
      this.catalog_id === other.catalog_id
    );
  }

  static createWithData(
    data: ObservationBlob | ObservationTypeBlob,
    selected: boolean = false
  ) {
    return new Observation({
      ...data,
      selected,
    });
  }

  isSelected() {
    return this.selected;
  }

  static select(observation: Observation): Observation {
    return new Observation({
      ...observation,
      selected: true,
    });
  }

  static deselect(observation: Observation): Observation {
    return new Observation({
      ...observation,
      selected: false,
    });
  }

  static toggle(observation: Observation): Observation {
    return new Observation({
      ...observation,
      selected: !observation.selected,
    });
  }

  static isSelected(observation: Observation): boolean {
    return observation.selected;
  }

  static wasMadeByPowerReviews(observation: Observation): boolean {
    const { client_id = null } = observation;
    return Number(client_id) === 1;
  }

  static wasMadeByClient(observation: Observation): boolean {
    return !Observation.wasMadeByPowerReviews(observation);
  }

  static addComments(observation: Observation, comments: string): Observation {
    return new Observation({
      ...observation,
      observation_data: { comments },
    });
  }

  static Contradict = 'CC';
  static RetailComp = 'CR';
  static Copyright = 'CV';
  static Duplicate = 'DC';
  static Fraud = 'FRD';
  static Legal = 'LC';
  static Irrelevant = 'NR';
  static PII = 'PII';
  static Profane = 'PIM';
  static Price = 'PR';
  static SafetyAlert = 'SA';
  static SafetyConcern = 'SC';
  static Test = 'TST';
  static Url = 'URL';
  static Web = 'WEB';
  static ForeignLang = 'FL';
  static Underage = 'US';
  static CSAlert = 'CS';
  static ClientEscalated = 'CE';
  // special observations
  static PowerModerationCode = 'PM';
  static SecondOpinionCode = 'SO';
  static ClientModerationCode = 'CM';
  static LegacyRejectedCode = 'LR';
  static ConsumerFlaggedCode = 'CF';
  static EscalatedWordList = 'EWL';
  static MissingDeviceData = 'MDD';
  static ImportRejected = 'IR';
  static ImportApproved = 'IA';
  static PublicationOverrideCode = 'PO';
  static NotForPublicationOverrideCode = 'NPO';
  static ShowServiceComment = 'SSC';
  static BrandEngaged = 'BE';
  static ConsumerNotifiedOfRejection = 'CNR';

  static MetaObservationTypeCodes: Set<ObservationTypeCode> = new Set([
    Observation.PowerModerationCode,
    Observation.SecondOpinionCode,
    Observation.ClientModerationCode,
    Observation.EscalatedWordList,
    Observation.MissingDeviceData,
    Observation.ImportRejected,
    Observation.ImportApproved,
    Observation.PublicationOverrideCode,
    Observation.NotForPublicationOverrideCode,
    Observation.BrandEngaged,
    Observation.ConsumerNotifiedOfRejection,
  ]);

  static SortOrder = [
    Observation.Contradict,
    Observation.RetailComp,
    Observation.Copyright,
    Observation.Duplicate,
    Observation.Underage,
    Observation.Fraud,
    Observation.Legal,
    Observation.Irrelevant,
    Observation.PII,
    Observation.Profane,
    Observation.Price,
    Observation.SafetyAlert,
    Observation.SafetyConcern,
    Observation.Test,
    Observation.Url,
    Observation.Web,
    Observation.ForeignLang,
    Observation.CSAlert,
    Observation.ClientEscalated,
  ];

  static SortOrderMap = Map(
    Observation.SortOrder.reduce((acc, code, index) => {
      acc[code] = index;
      return acc;
    }, {})
  );

  static comparator = (lhs: Comparable, rhs: Comparable) => {
    const defaultSortOrder = Observation.SortOrderMap.count();
    const lhsOrder = Observation.SortOrderMap.get(
      typeof lhs === 'string' ? lhs : lhs.observation_type_code,
      defaultSortOrder
    );
    const rhsOrder = Observation.SortOrderMap.get(
      typeof rhs === 'string' ? rhs : rhs.observation_type_code,
      defaultSortOrder
    );

    return lhsOrder - rhsOrder;
  };

  static EnumerableMetaObservationTypeCodes: Set<ObservationTypeCode> = new Set(
    [Observation.ConsumerFlaggedCode, Observation.LegacyRejectedCode]
  );

  static IsEditableObservationTypeCode = (code: ObservationTypeCode): boolean =>
    Observation.MetaObservationTypeCodes.has(code) === false &&
    Observation.EnumerableMetaObservationTypeCodes.has(code) === false;

  static IsEditableObservation = (ob: Observation): boolean =>
    Observation.IsEditableObservationTypeCode(ob.observation_type_code);

  static IsEnumerableObservationTypeCode = (
    code: ObservationTypeCode
  ): boolean => Observation.MetaObservationTypeCodes.has(code) === false;

  static IsEnumerableObservation = (ob: Observation): boolean =>
    Observation.IsEnumerableObservationTypeCode(ob.observation_type_code);
}



// WEBPACK FOOTER //
// ./src/models/Observation.js